import { DisplayAttachmentHistory } from '@/components/attachment-uploader/display-attachment-history'
import { monthOptions, yearOptions } from '@/components/attachment-uploader/utils'
import { FileInput } from '@/shared/components/file-input'
import { toOptions } from '@/utils'
import { DOCUMENT_UPLOAD_ACCEPT, validateDocumentFile } from '@/utils/document-upload'
import { Input, Typography } from '@material-tailwind/react'
import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CustomSelect from '@/shared/components/custom-select'
import { FileInputSection } from './attachment-uploader.styled'
import { ATTACHMENT_STATUSES } from '@/constants'
import moment from '@/config/moment'
import { toast } from 'react-toastify'

const initialStates = Object.freeze({
  file: {
    data: '',
    filename: '',
    content_type: '',
    identify: false
  }
})

export const AttachmentUploader = (props) => {
  const {
    attachmentTypes,
    attachableType,
    onUpload,
    children,
    showLatestAttachments,
    attachments,
    initialValues,
    showDefaultHeader,
    isDisabled,
    restrictPastExpirationDates,
    expirationDateNotice
  } = props
  const { t } = useTranslation()
  const minExpirationDate = useMemo(() => {
    if (!restrictPastExpirationDates) return undefined

    return moment().add(1, 'day').format('YYYY-MM-DD')
  }, [restrictPastExpirationDates])

  const latestAttachments = useMemo(() => {
    return attachments.filter(attachment => attachment.status !== 'pending_upload').slice(0, 5)
  }, [attachments])

  // memos
  const filteredAttachmentTypes = useMemo(() => {
    if (!attachableType) return attachmentTypes

    return attachmentTypes.filter((attachmentType) => {
      const klass = attachmentType?.klass || attachmentType?.resource?.klass
      return klass === attachableType
    })
  }, [attachmentTypes, attachableType])

  const attachmentTypeOptions = useMemo(() => {
    return toOptions({ data: filteredAttachmentTypes })
  }, [filteredAttachmentTypes])

  // formik
  const {
    values,
    setFieldValue,
    handleChange,
    handleSubmit,
    resetForm
  } = useFormik({ initialValues, onSubmit: onUpload, enableReinitialize: true })
  const showExpirationWarning = useMemo(() => {
    if (!expirationDateNotice) return false
    if (!values.expiration_date) return false

    const selectedDate = moment(values.expiration_date, 'YYYY-MM-DD', true)
    if (!selectedDate.isValid()) return false

    const daysUntilExpiration = selectedDate.startOf('day').diff(moment().startOf('day'), 'days')
    return daysUntilExpiration < 7
  }, [expirationDateNotice, values.expiration_date])

  // states
  const [selectedAttachmentType, setSelectedAttachmentType] = useState(
    attachmentTypeOptions.find(({ value }) => value === values.attachment_type_id) || {}
  )

  const [file, setFile] = useState(initialStates.file)
  const [isNotApplicableLocked, setIsNotApplicableLocked] = useState(false)

  useEffect(() => {
    if (!selectedAttachmentType.value) {
      setIsNotApplicableLocked(false)
      return
    }
    setFile(initialStates.file)

    if (selectedAttachmentType.resource.recurrence === 'monthly') {
      if (!values.period_month) return
      if (!values.period_year) return
    }

    if (selectedAttachmentType.resource.recurrence === 'yearly') {
      if (!values.period_year) return
    }

    const [currentAttached = {}] = attachments.filter(attachment => {
      return attachment.periodMonth === values.period_month &&
        attachment.periodYear === values.period_year &&
        attachment.attachmentType.id === values.attachment_type_id
    })

    if (!currentAttached.id) {
      setIsNotApplicableLocked(false)
      return
    }

    const isLocked = currentAttached.status === ATTACHMENT_STATUSES.notApplicable
    setIsNotApplicableLocked(isLocked)

    const newFile = {
      data: currentAttached.documentInfo.data,
      filename: currentAttached.documentInfo.filename,
      content_type: currentAttached.documentInfo.content_type,
      identify: false
    }

    setFieldValue('id', currentAttached.id)
    const shouldDropExpiration =
      currentAttached.status &&
      !['pending', 'pending_upload'].includes(currentAttached.status)
    setFieldValue(
      'expiration_date',
      shouldDropExpiration ? null : currentAttached.expirationDate
    )
    setFieldValue(
      'status',
      isLocked ? ATTACHMENT_STATUSES.notApplicable : ATTACHMENT_STATUSES.pending
    )
    setFile(newFile)
  }, [
    values.period_month,
    values.period_year,
    values.attachment_type_id
  ])

  useEffect(() => {
    if (!selectedAttachmentType.value) return

    resetForm()
    setFieldValue('attachment_type_id', selectedAttachmentType.value)
    setFile(initialStates.file)
    setIsNotApplicableLocked(false)
  }, [selectedAttachmentType])

  useEffect(() => {
    setFieldValue('document', file)
  }, [file])

  const handleSubmitWithGuard = () => {
    if (isNotApplicableLocked) return

    if (
      restrictPastExpirationDates &&
      selectedAttachmentType?.resource?.recurrence === 'once' &&
      values.expiration_date &&
      values.expiration_date < minExpirationDate
    ) {
      toast.warn(t('common.uploadDocuments.invalidExpirationDate'))
      return
    }

    handleSubmit()
  }

  return (
    <form>
      <div className='flex flex-col gap-6'>
        {
          showDefaultHeader &&
          <div className='flex flex-col gap-6'>
            <section className='flex flex-col'>
              <Typography
                variant='h4'
                className='text-gray-900 font-semibold not-italic'
              >
                Cargador de documentos
              </Typography>

              <Typography variant='paragraph' className='text-gray-500'>
                Selecciona y sube o sobrescribe los documentos.
              </Typography>
            </section>
          </div>
        }

        <CustomSelect
          placeholder='Selecciona un documento'
          options={attachmentTypeOptions}
          onChange={setSelectedAttachmentType}
          value={selectedAttachmentType}
          isLoading={!attachmentTypes.length}
          isDisabled={isDisabled}
        />
        <div className='grid grid-cols-2 gap-4'>
          {

            selectedAttachmentType?.resource?.recurrence === 'monthly' &&
            <CustomSelect
              isDisabled={!selectedAttachmentType.value || isDisabled}
              placeholder='Selecciona un mes'
              options={monthOptions}
              onChange={({ value }) => setFieldValue('period_month', value)}
              value={monthOptions.find(({ value }) => value === values.period_month) || ''}
            />
          }

          {
            ['monthly', 'yearly'].includes(selectedAttachmentType?.resource?.recurrence) &&
            <CustomSelect
              isDisabled={!selectedAttachmentType.value || isDisabled}
              placeholder='Selecciona un año'
              options={yearOptions()}
              onChange={({ value }) => setFieldValue('period_year', value)}
              value={yearOptions().find(({ value }) => value === values.period_year) || ''}
            />
          }
        </div>

        <section className='grid grid-cols-12 gap-4'>
          <FileInputSection fullWidth={!showLatestAttachments}>
            <Typography
              variant='paragraph'
              className='font-semibold text-gray-900'
            >
              Nuevo archivo
            </Typography>
            {
              isNotApplicableLocked
                ? (
                  <Typography variant='small' className='text-gray-600'>
                    Este documento está marcado como no aplica. No se puede subir un archivo.
                  </Typography>
                  )
                : (
                  <FileInput
                    className='flex flex-col items-center justify-center'
                    handleChange={setFile}
                    value={file}
                    accept={DOCUMENT_UPLOAD_ACCEPT}
                    validateFile={validateDocumentFile}
                    onInvalidFile={(message) => toast.warn(message)}
                  />
                  )
            }
          </FileInputSection>

          {
            showLatestAttachments &&
            <article className='col-span-4 flex flex-col gap-4'>
              <Typography
                variant='paragraph'
                className='font-semibold text-gray-900 p-0'
              >
                Últimos archivos cargados
              </Typography>
              <DisplayAttachmentHistory attachments={latestAttachments} />
            </article>
          }

          {selectedAttachmentType?.resource?.recurrence === 'once' && (
            <article className='col-span-12 flex flex-col gap-5'>
              <Typography
                variant='paragraph'
                className='font-semibold text-gray-900'
              >
                {t('common.uploadDocuments.expirationDate')}
              </Typography>
              <div className='flex flex-col gap-4 md:flex-row md:items-start'>
                <div className='md:w-72 md:flex-none'>
                  <Input
                    name='expiration_date'
                    type='date'
                    value={values.expiration_date || ''}
                    label='Seleccione una fecha'
                    onChange={handleChange}
                    min={minExpirationDate}
                  />
                </div>
                {showExpirationWarning && (
                  <Typography variant='small' className='text-red-700 md:flex-1'>
                    {expirationDateNotice}
                  </Typography>
                )}
              </div>
            </article>
          )}
        </section>

        {children(handleSubmitWithGuard)}
      </div>
    </form>
  )
}

AttachmentUploader.propTypes = {
  attachmentTypes: PropTypes.array,
  attachableType: PropTypes.string,
  showLatestAttachments: PropTypes.bool,
  attachments: PropTypes.array,
  onUpload: PropTypes.func,
  children: PropTypes.node,
  initialValues: PropTypes.object,
  showDefaultHeader: PropTypes.bool,
  isDisabled: PropTypes.bool,
  restrictPastExpirationDates: PropTypes.bool,
  expirationDateNotice: PropTypes.string
}

AttachmentUploader.defaultProps = {
  showLatestAttachments: false,
  attachments: [],
  attachmentTypes: [],
  attachableType: null,
  initialValues: {
    id: null,
    attachment_type_id: null,
    document: {},
    expiration_date: null,
    period_month: null,
    period_year: null,
    status: 'pending'
  },
  showDefaultHeader: true,
  isDisabled: false,
  restrictPastExpirationDates: false,
  expirationDateNotice: null
}
